/**
 * src/components/map/MapEntityTooltip.tsx
 *
 * The card that appears when you hover something on the map.
 *
 * One shell, two kinds of thing inside it. The country tooltip has been
 * the only map hover card for long enough that its appearance *is* what
 * a hover card looks like in this app — the dark glass panel with the
 * coloured stripe down its left edge — and a spatial object arriving
 * with its own invented styling would read as a different application.
 * So the chrome moved out here verbatim, and both consumers pass their
 * own header and body into it.
 *
 * The shell owns appearance, position and the pointer grace period.
 * It owns no data: it does not know what a country is, and it must not
 * learn what a spatial object is either. Whatever is worth saying about
 * the thing being hovered is the caller's business, which is what keeps
 * spatial objects out of country-shaped data structures.
 *
 * Nothing here fetches anything. A hover card that made a request would
 * make dragging across a city a denial of service against our own API,
 * so the rule is that hovering costs nothing and only a deliberate
 * click is allowed to load.
 */

import React from 'react';
import { placeMapTooltip, mainMapControlZones, type Point, type Rect } from './mapTooltipPlacement';
import { useMapSurface } from './mapOverlayStore';
import { surfaceZIndex, type MapSurfaceKind } from './mapSurfaces';

export interface MapEntityTooltipProps {
  /** Cursor position in map-container pixels, as Mapbox reports it. */
  anchor: Point;
  /** Map container size. Falls back to the window when unmeasurable. */
  viewport?: { width: number; height: number } | null;
  /** The left stripe, and the status dot unless one is given separately. */
  accentColor: string;
  /** Small element before the title: a flag, a type glyph. */
  leading?: React.ReactNode;
  title: React.ReactNode;
  /** Short chip after the title: a country code, an object type. */
  badge?: React.ReactNode;
  /** Overrides the accent for the dot at the top right. Null hides it. */
  statusColor?: string | null;
  /** Defaults to the country card's width, which is what it was tuned at. */
  width?: number;
  /** Approximate height, used only to keep the card off the controls. */
  estimatedHeight?: number;
  /**
   * Screen furniture to avoid, overriding the coordinator entirely.
   *
   * Rarely wanted. Passing it opts out of knowing about other open
   * surfaces, which is the thing that stops two cards landing on each
   * other; it exists for a host that has no coordinator and for tests
   * that want to pin the arithmetic down without a provider.
   */
  avoid?: readonly Rect[];
  /**
   * What this card is, to the overlay coordinator.
   *
   * Given both, the card registers itself as an open surface and takes
   * its avoid zones from every surface that outranks it. Omitted, it
   * behaves as it always did and avoids the control clusters only —
   * which is why an unconverted caller does not regress.
   */
  surfaceId?: string;
  surfaceKind?: MapSurfaceKind;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children?: React.ReactNode;
  'data-testid'?: string;
}

/** The country card's dimensions, kept because they were tuned by eye. */
export const TOOLTIP_WIDTH = 284;
export const TOOLTIP_ESTIMATED_HEIGHT = 340;

export const MapEntityTooltip: React.FC<MapEntityTooltipProps> = ({
  anchor,
  viewport = null,
  accentColor,
  leading = null,
  title,
  badge = null,
  statusColor,
  width = TOOLTIP_WIDTH,
  estimatedHeight = TOOLTIP_ESTIMATED_HEIGHT,
  avoid,
  surfaceId,
  surfaceKind,
  onMouseEnter,
  onMouseLeave,
  children,
  'data-testid': testId,
}) => {
  const size = { width, height: estimatedHeight };
  const bounds = viewport && viewport.width > 0 && viewport.height > 0
    ? viewport
    : { width: window.innerWidth, height: window.innerHeight };

  // The control clusters are this application's own furniture, so they
  // are contributed here rather than known to the coordinator. They
  // stay in the avoid set either way: a card over the hamburger is the
  // failure that actually gets reported.
  const furniture = React.useMemo(() => mainMapControlZones(bounds), [bounds.width, bounds.height]); // eslint-disable-line react-hooks/exhaustive-deps

  // Where this card last landed, reported back so lower-ranked cards
  // can avoid it. Held in state rather than derived, because it is the
  // output of placement and placement is what consumes the avoid set.
  const [placed, setPlaced] = React.useState<Rect | null>(null);

  const { avoid: coordinated } = useMapSurface({
    id: surfaceId ?? '',
    kind: surfaceKind ?? 'object_tooltip',
    rect: placed,
    furniture,
    enabled: !!surfaceId && !!surfaceKind,
  });

  const { left, top } = placeMapTooltip({
    anchor,
    viewport: bounds,
    size,
    avoid: avoid ?? (surfaceId && surfaceKind ? coordinated : furniture),
  });

  // Report the resulting rectangle once it settles. Comparing first
  // keeps an unchanged placement from scheduling another render, and
  // the store discards an identical rectangle as well.
  React.useEffect(() => {
    if (!surfaceId) return;
    setPlaced((prev) => {
      const next: Rect = {
        left, top, right: left + width, bottom: top + estimatedHeight,
      };
      if (prev && prev.left === next.left && prev.top === next.top
        && prev.right === next.right && prev.bottom === next.bottom) {
        return prev;
      }
      return next;
    });
  }, [surfaceId, left, top, width, estimatedHeight]);

  const dot = statusColor === undefined ? accentColor : statusColor;

  return (
    <div
      data-testid={testId}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'absolute', left: `${left}px`, top: `${top}px`, width: `${width}px`,
        backgroundColor: 'rgba(8, 13, 24, 0.97)', color: '#fff',
        borderRadius: '12px', fontSize: '13px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        pointerEvents: 'auto',
        // Repositioning is the primary mechanism; this is what happens
        // when a viewport is too small for anything to clear, and the
        // cards have to overlap after all. Coordinated surfaces stack
        // by rank so the deliberate one is on top, and all of them stay
        // below the application's own modals at 10000 and above — a
        // dialog should cover a hover card, not lose to one. An
        // unconverted caller keeps the flat 10000 it always had.
        zIndex: surfaceKind ? surfaceZIndex(surfaceKind) : 10000,
        boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        backdropFilter: 'blur(16px)',
        borderTopWidth: '1px', borderTopStyle: 'solid', borderTopColor: 'rgba(255,255,255,0.1)',
        borderRightWidth: '1px', borderRightStyle: 'solid', borderRightColor: 'rgba(255,255,255,0.1)',
        borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'rgba(255,255,255,0.1)',
        borderLeftWidth: '3px', borderLeftStyle: 'solid', borderLeftColor: accentColor,
        overflow: 'visible',
      }}
    >
      <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
            {leading}
            <span style={{ fontWeight: 700, fontSize: '14px', color: '#f0f4ff', lineHeight: 1.2 }}>
              {title}
            </span>
            {badge}
          </div>
          {dot && (
            <div
              style={{
                width: '8px', height: '8px', borderRadius: '50%',
                background: dot, boxShadow: `0 0 6px ${dot}`, flexShrink: 0,
              }}
            />
          )}
        </div>
      </div>

      {children}
    </div>
  );
};

/** The chip used for a country code, and now for an object's type. */
export const tooltipBadgeStyle: React.CSSProperties = {
  fontSize: '10px',
  fontWeight: 700,
  color: '#94a3b8',
  background: 'rgba(255,255,255,0.1)',
  borderRadius: '4px',
  padding: '1px 5px',
  letterSpacing: '0.06em',
};

export default MapEntityTooltip;
